/**
 * Welcome Screen Component
 * Clean empty state shown when there are no messages
 */
import { useMemo, useState } from "react";
import type { FileUIPart } from "ai";
import { Suggestion } from "@/components/ai-elements/suggestion";
import { ConversationEmptyState } from "@/components/ai-elements/conversation";
import { PlanPanel } from "@/components/rich-ui/PlanPanel";
import { RichClaimCards } from "@/components/rich-ui/RichClaimCards";
import { SubAgentRoster } from "@/components/rich-ui/SubAgentRoster";
import { MOCK_CLAIMS, MOCK_PLAN_STEPS } from "@/shared/mock-data";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";

/** Starter suggestions for ClaimMosaic users */
const STARTER_SUGGESTIONS = [
  "Find lead Jamie Smith and convert it to a claim",
  "What documentation gaps are blocking payout on my latest claim?",
  "Link the right contact and insurance company to claim CM-2026-00177",
  "Add a claim note and upload a file to my latest claim"
];

interface WelcomeScreenProps {
  /** Callback when a suggestion is clicked */
  onSuggestionClick?: (suggestion: string) => void;
  /** Callback when policy review launcher submits */
  onPolicyReviewLaunch?: (payload: {
    objective: string;
    filePart: FileUIPart;
  }) => Promise<void> | void;
  /** Whether demo rich UI widgets should be rendered */
  showDemoRichUI?: boolean;
}

async function fileToDataUrl(file: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const value = typeof reader.result === "string" ? reader.result : "";
      resolve(value);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Unable to read file"));
    reader.readAsDataURL(file);
  });
}

export function WelcomeScreen({
  onSuggestionClick,
  onPolicyReviewLaunch,
  showDemoRichUI = false
}: WelcomeScreenProps) {
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [objective, setObjective] = useState(
    "Review this policy and identify coverage, exclusions, and key findings."
  );
  const [isLaunching, setIsLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const canLaunch = useMemo(
    () => Boolean(selectedFile) && objective.trim().length > 0 && !isLaunching,
    [selectedFile, objective, isLaunching]
  );

  const launchPolicyReview = async () => {
    if (!selectedFile || !onPolicyReviewLaunch) {
      return;
    }

    setLaunchError(null);
    setIsLaunching(true);
    try {
      const dataUrl = await fileToDataUrl(selectedFile);
      await onPolicyReviewLaunch({
        objective: objective.trim(),
        filePart: {
          type: "file",
          filename: selectedFile.name,
          mediaType: selectedFile.type || "application/pdf",
          url: dataUrl
        }
      });
      setPolicyDialogOpen(false);
      setSelectedFile(null);
    } catch (error) {
      setLaunchError(
        error instanceof Error ? error.message : "Failed to launch policy review."
      );
    } finally {
      setIsLaunching(false);
    }
  };

  return (
    <ConversationEmptyState className="h-full py-6">
      <div className="space-y-6 max-w-4xl mx-auto w-full">
        <div className="text-center space-y-2">
          <div className="inline-flex">
            <img
              src="/logo-bird.svg"
              alt="ClaimMosaic Agent"
              className="w-20 h-20"
            />
          </div>
          <h3 className="font-semibold text-lg text-foreground">
            ClaimMosaic Agent
          </h3>
          {showDemoRichUI ? (
            <p className="text-muted-foreground text-sm">
              Multi-agent claim workflows with rich UI previews for specialist
              analysis
            </p>
          ) : (
            <p className="text-muted-foreground text-sm">
              Multi-agent claim workflows for public adjusters
            </p>
          )}
        </div>

        {showDemoRichUI && (
          <>
            <PlanPanel title="Claim Plan" steps={MOCK_PLAN_STEPS} />
            <RichClaimCards claims={MOCK_CLAIMS} />
            <SubAgentRoster />
          </>
        )}

        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <h4 className="font-medium text-sm">Policy Review Specialist</h4>
              <p className="text-xs text-muted-foreground">
                Upload a policy PDF and launch coverage review context.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => setPolicyDialogOpen(true)}
              disabled={!onPolicyReviewLaunch}
            >
              Launch Policy Review
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-2 max-w-2xl mx-auto pt-1">
          {STARTER_SUGGESTIONS.map((suggestion) => (
            <Suggestion
              key={suggestion}
              suggestion={suggestion}
              onClick={() => onSuggestionClick?.(suggestion)}
              className="whitespace-normal text-center h-auto"
            />
          ))}
        </div>
      </div>

      <Dialog open={policyDialogOpen} onOpenChange={setPolicyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Launch Policy Review</DialogTitle>
            <DialogDescription>
              Upload the policy PDF and specify the objective.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <label htmlFor="policy-review-file" className="text-sm font-medium">
                Policy PDF
              </label>
              <Input
                id="policy-review-file"
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) => {
                  const file = event.currentTarget.files?.[0] ?? null;
                  setSelectedFile(file);
                }}
              />
              {selectedFile ? (
                <p className="text-xs text-muted-foreground">{selectedFile.name}</p>
              ) : null}
            </div>
            <div className="space-y-1">
              <label htmlFor="policy-review-objective" className="text-sm font-medium">
                Objective
              </label>
              <Textarea
                id="policy-review-objective"
                rows={4}
                value={objective}
                onChange={(event) => setObjective(event.currentTarget.value)}
              />
            </div>
            {launchError ? (
              <p className="text-xs text-destructive">{launchError}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPolicyDialogOpen(false)}
              disabled={isLaunching}
            >
              Cancel
            </Button>
            <Button onClick={launchPolicyReview} disabled={!canLaunch}>
              {isLaunching ? "Launching..." : "Start"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConversationEmptyState>
  );
}
